import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { MessageSquare, Minus, Plus, Loader2, RefreshCw } from "lucide-react";
import { useSmsQuota } from "@/hooks/useSmsQuota";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const PACK_SIZE = 50;
const PACK_PRICE_PENCE = 999;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

const SmsUsageCard = () => {
  const { quota, loading, refresh } = useSmsQuota();
  const [quantity, setQuantity] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const reconcile = async (showToast: boolean) => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("reconcile-sms-topups");
      if (error) throw new Error(error.message);
      if (data?.credited > 0) {
        toast.success(`Credited ${data.sms_added} SMS from ${data.credited} purchase${data.credited > 1 ? "s" : ""}.`);
        await refresh();
      } else if (showToast) {
        toast.info("No pending top-ups found.");
      }
    } catch (err: any) {
      if (showToast) toast.error(err?.message || "Could not sync purchases");
      else console.warn("reconcile failed", err?.message);
    } finally {
      setSyncing(false);
    }
  };

  // Auto-reconcile on mount and when returning from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isSuccess = params.get("sms_topup") === "success";
    reconcile(isSuccess);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      const { data, error } = await supabase.functions.invoke("purchase-sms-topup", {
        body: { quantity },
      });
      if (error) throw new Error(error.message);
      if (data?.url) {
        const win = window.open(data.url, "_blank", "noopener,noreferrer");
        if (!win) {
          try {
            window.top!.location.href = data.url;
          } catch {
            window.location.href = data.url;
          }
        }
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      toast.error(err?.message || "Could not start checkout");
    } finally {
      setPurchasing(false);
    }
  };

  const totalPence = quantity * PACK_PRICE_PENCE;
  const totalSms = quantity * PACK_SIZE;
  const totalPriceFormatted = `£${(totalPence / 100).toFixed(2)}`;

  const usedPct = quota
    ? Math.min(100, Math.round((quota.used_this_cycle / Math.max(quota.base_allowance, 1)) * 100))
    : 0;

  return (
    <Card id="sms-usage" className="scroll-mt-20">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              SMS Usage
            </CardTitle>
            <CardDescription>
              Your plan includes 50 SMS per billing cycle. Unused credits roll over and top-ups never expire.
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => reconcile(true)}
            disabled={syncing}
            aria-label="Sync recent purchases"
          >
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-2 hidden sm:inline">Sync purchases</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading || !quota ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-baseline justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Used this cycle</p>
                  <p className="text-2xl font-semibold">
                    {quota.used_this_cycle}
                    <span className="text-base font-normal text-muted-foreground"> / {quota.base_allowance}</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Total remaining</p>
                  <p className="text-2xl font-semibold">{quota.total_remaining}</p>
                </div>
              </div>
              <Progress value={usedPct} className="h-2" />
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="text-muted-foreground">Cycle left</p>
                  <p className="font-semibold text-sm">{quota.base_remaining}</p>
                </div>
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="text-muted-foreground">Rollover</p>
                  <p className="font-semibold text-sm">+{quota.rollover_balance}</p>
                </div>
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="text-muted-foreground">Top-up</p>
                  <p className="font-semibold text-sm">+{quota.topup_balance}</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Cycle ends {formatDate(quota.cycle_end)}. Any unused cycle credits roll over to next month.
              </p>
            </div>

            <div className="rounded-lg border p-4 space-y-4">
              <div>
                <h4 className="font-medium">Buy more SMS</h4>
                <p className="text-sm text-muted-foreground">
                  £9.99 per 50 SMS pack. Credits never expire.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={purchasing || quantity <= 1}
                    aria-label="Decrease quantity"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <div className="min-w-[80px] text-center">
                    <p className="text-xl font-semibold leading-none">{quantity}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      pack{quantity > 1 ? "s" : ""}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity((q) => Math.min(20, q + 1))}
                    disabled={purchasing || quantity >= 20}
                    aria-label="Increase quantity"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm text-muted-foreground">
                    {totalSms} SMS total
                  </p>
                  <p className="text-xl font-semibold">{totalPriceFormatted}</p>
                </div>

                <Button onClick={handlePurchase} disabled={purchasing} size="lg" className="ml-auto">
                  {purchasing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Opening checkout…
                    </>
                  ) : (
                    `Buy ${totalSms} SMS`
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SmsUsageCard;
